import { useEffect } from 'react';

// --- Central brand constants (SEO plan §9, §12) ---------------------------
// GlobalEPRC is the primary public-facing brand. "GEPRC" is kept only as a
// secondary abbreviation because another established FPV/drone business
// already occupies that abbreviation in search — see plan §12.
export const SITE_NAME = 'GlobalEPRC';
export const FULL_NAME = 'Global Ethical People Resourcing Consultancy';
export const BASE_URL = 'https://globaleprc.com';
export const LOGO_URL = `${BASE_URL}/logo.png`;

// Real profile URLs are not wired up anywhere in this codebase yet
// (the footer's social icons all point to "#"). Per the SEO plan, never
// publish fake URLs in structured data — replace these before shipping.
export const SOCIAL_LINKS = {
  linkedin: 'REPLACE-WITH-REAL-LINKEDIN-URL',
  facebook: 'REPLACE-WITH-REAL-FACEBOOK-URL',
  instagram: 'REPLACE-WITH-REAL-INSTAGRAM-URL',
  youtube: 'REPLACE-WITH-REAL-YOUTUBE-URL',
};

function setMetaTag(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * SEO — drop at the top of any routed page's JSX to give that page a
 * unique <title>, meta description, canonical URL, and (optionally)
 * JSON-LD structured data. Implements plan §1 ("unique title/H1/meta
 * description for every important page") without adding any new
 * dependency (no react-helmet), since this project's package.json isn't
 * part of this src/ export.
 *
 * IMPORTANT LIMITATION (plan §6, JS/SPA crawlability):
 * This is a client-side-rendered Create React App. These tags are only
 * written to <head> after this component's JS runs in the browser, not
 * in the raw HTML response. Google generally renders JS before indexing,
 * so this still helps Google — but anything that reads raw HTML without
 * executing JS (some social-share link previews, some crawlers/bots)
 * will not see them. The durable fix is server-side rendering or
 * build-time prerendering (e.g. Next.js, or a prerender step such as
 * react-snap), which requires touching the build config / package.json
 * that wasn't included in this export.
 *
 * @param {string} title - Full <title> text, e.g. "Page Name | GlobalEPRC"
 * @param {string} description - Meta description (~150-160 chars)
 * @param {string} path - Site-relative path used to build the canonical URL, e.g. "/about"
 * @param {object|object[]} [jsonLd] - One or more JSON-LD objects to inject (homepage/brand page only, per plan §2-3)
 */
const SEO = ({ title, description, path, jsonLd }) => {
  useEffect(() => {
    if (title) document.title = title;
    setMetaTag('name', 'description', description);
    setCanonical(`${BASE_URL}${path || ''}`);

    const ldIds = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item, i) => {
        const id = `ld-json-${i}`;
        setJsonLd(id, item);
        ldIds.push(id);
      });
    }

    // Clean up JSON-LD injected by this page when navigating away, so
    // schema from one page never lingers and describes the next page.
    return () => {
      ldIds.forEach(removeJsonLd);
    };
  }, [title, description, path, jsonLd]);

  return null;
};

export default SEO;
